/* GDPR info wrapper
 *
 * This class provides access to GDPR-related data that comes from our
 * backend and populates mp.globals.gdpr. If these globals are not present,
 * a fallback is provided that can be accessed via the same interface.
 *
 * GDPR data can be modified via gdpr.update({data}). This will modify the
 * underlying data object, whether that's mp.globals.gdpr or the fallback
 * gdpr._data object.
 *
 * Attributes:
 *   isGdpr
 *     - true if the user is in a GDPR-relevant country (based on IP)
 *   optTracking
 *     - "in"|"out"|"undecided", the user's current opt-status for tracking
 *   optCookies
 *     - "in"|"out"|"undecided", the user's current opt-status for cookies
 *   allowTracking
 *     - true if user is either
 *       - in a GDPR-relevant country and optTracking is "in"
 *       - in a non-GDPR-relevant country and optTracking is not "out"
 *   allowStorage
 *     - true if user is either
 *       - in a GDPR-relevant country and optStorage is "in"
 *       - in a non-GDPR-relevant country and optStorage is not "out"
 *   allowSnippets
 *     - indicates whether vendor snippets can be executed
 *     - true if allowTracking and allowStorage are true
 *   showBanner
 *     - indicates whether the GDPR opt-in banner should be displayed
 *     - true if all of the following are true
 *       - user is in GDPR-relevant country
 *       - optTracking is not "in" or "out"
 *       - optCookies is not "in" or "out"
 */
class Gdpr {
  constructor() {
    this._data = {};
  }

  get _gdprData() {
    return this._data;
  }

  update(gdprData) {
    Object.assign(this._gdprData, gdprData);
  }

  get isGdpr() {
    return this._gdprData[`is_gdpr`];
  }

  get optTracking() {
    return this._gdprData[`opt_tracking`];
  }

  get optCookies() {
    return this._gdprData[`opt_cookies`];
  }

  get allowTracking() {
    return (
      // if GDPR doesn't apply, allow tracking if user hasn't opted out
      // if GDPR applies, only allow tracking if user has explicitly opted in
      (!this.isGdpr && this.optTracking !== `out`) || (this.isGdpr && this.optTracking === `in`)
    );
  }

  get allowStorage() {
    return (
      // if GDPR doesn't apply, allow cookies if user hasn't opted out
      // if GDPR applies, only allow cookies if user has explicitly opted in
      (!this.isGdpr && this.optCookies !== `out`) || (this.isGdpr && this.optCookies === `in`)
    );
  }

  get allowSnippets() {
    return this.allowTracking && this.allowStorage;
  }

  get showBanner() {
    return this.isGdpr && ![`in`, `out`].includes(this.optTracking) && ![`in`, `out`].includes(this.optCookies);
  }
}

export const gdpr = new Gdpr();
